import { Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { Loading } from 'ra-ui-materialui'
import { getPropertyDetail } from '@api'
import { selectedGroup, selectedProperty } from '@reducer/profile'
import usePortfolioUrlScope from '@hooks/usePortfolioUrlScope'
import { IEntity } from '@/types'

/**
 * Route layer for /b/:bId/*: the URL is the source of truth for the selected
 * building. When it disagrees with the persisted redux profile (shared link,
 * back/forward, second tab), the entity is fetched and redux re-pointed at it,
 * so every child page keeps reading redux exactly as before.
 */
export const BuildingScope = () => {
  const { bId = '' } = useParams()
  const dispatch = useDispatch()
  const { selectedPropertyId, selectedGroupId } = useSelector((state) => state.profile)

  useEffect(() => {
    // A matching building is not enough to skip: persisted profiles can carry
    // a building with an empty group (legacy clearGroup blobs), and every
    // group-scoped payload built from that state silently no-ops server-side.
    if (!bId || (bId === selectedPropertyId && selectedGroupId)) return
    getPropertyDetail<IEntity>({ eId: bId }).then((res) => {
      if (!res?.BuildingId) return
      if (res.GroupId && res.GroupId !== selectedGroupId) {
        // Cross-group link: re-point the group too. The name is not on the
        // entity payload; the account search fills it in when next opened.
        dispatch(selectedGroup({ groupId: res.GroupId, groupName: '', group: {} }))
      }
      dispatch(
        selectedProperty({
          propertyId: res.BuildingId,
          propertyName: res.Name?.en || '',
          buildingCode: res.Code,
          propertyTag: res.PropertyTag || {},
        })
      )
    })
  }, [bId, selectedPropertyId, selectedGroupId, dispatch])

  // Key the subtree on the building: switching buildings remounts the section
  // so it refetches on mount, matching the full navigation it was built for.
  return (
    <Fragment key={bId}>
      <Outlet />
    </Fragment>
  )
}

/**
 * Route layer for /p/:gId/*: the URL is the source of truth for the selected
 * portfolio, the way /b/:bId is for the building. When it disagrees with the
 * persisted redux profile (shared link, back/forward, second tab) the hook
 * switches to it: the session is re-pointed server-side for a member of
 * several portfolios, the group is looked up and selected for AcAdmin. A
 * portfolio the user may not open sends them to the same page on the
 * portfolio already selected, with the link's building ids dropped.
 *
 * The subtree is keyed on the portfolio so a switch remounts the page in
 * place and every child refetches for the new portfolio (specs#3464), and it
 * is held back while a switch is in flight: mounted early, a screen would read
 * the link's building ids (?bIds=) against the old portfolio and prune them.
 */
export const PortfolioScope = () => {
  const { gId = '' } = useParams()
  const { switching, redirectTo } = usePortfolioUrlScope(gId)
  if (redirectTo) return <Navigate to={redirectTo} replace />
  if (switching) return <Loading />
  return (
    <Fragment key={gId}>
      <Outlet />
    </Fragment>
  )
}

/**
 * The shim route's own splat, as a path suffix. react-admin mounts every route
 * under its own `/*`, and a route without a splat of its own inherits that
 * parent's `*` — the whole path — so appending it doubled the section
 * (/deficiencyTrackerGlobal → /p/:gId/deficiencyTrackerGlobal/deficiencyTrackerGlobal).
 * Only a shim on a `/*` route has a splat shorter than the path.
 */
const ownSplat = (splat: string | undefined, pathname: string) => {
  if (!splat) return ''
  let path = pathname
  try {
    // Params are decoded; the location pathname is not.
    path = decodeURI(pathname)
  } catch (e) {
    /* malformed escape: compare raw */
  }
  return `/${splat}` === path ? '' : `/${splat}`
}

/**
 * Shim for pre-/p/:gId portfolio URLs (bookmarks, emailed links, un-migrated
 * navigate() calls). Rebuilds the scoped path from the persisted redux
 * portfolio and preserves splat + query + router state.
 */
export const LegacyPortfolioRedirect = ({ to = '' }: { to?: string }) => {
  const params = useParams()
  const { pathname, search, hash, state } = useLocation()
  const { selectedGroupId } = useSelector((state) => state.profile)
  if (!selectedGroupId) return <Navigate to="/" replace />
  const section = to.replace(/\/+$/, '')
  const splat = ownSplat(params['*'], pathname)
  return <Navigate to={`/p/${selectedGroupId}${section}${splat}${search}${hash}`} state={state} replace />
}

/**
 * Shim for pre-/b/:bId URLs (bookmarks, emailed links, un-migrated navigate()
 * calls). Rebuilds the scoped path from the route's own :bId param when it has
 * one, else from the persisted redux building, and preserves splat + query.
 */
export const LegacyBuildingRedirect = ({ to = '' }: { to?: string }) => {
  const params = useParams()
  const { pathname, search, hash, state } = useLocation()
  const { selectedPropertyId } = useSelector((state) => state.profile)
  const bId = params.bId || selectedPropertyId
  if (!bId) return <Navigate to="/" replace />
  const section = to.replace(/:(\w+)\??/g, (_, name: string) => params[name] || '').replace(/\/+$/, '')
  const splat = ownSplat(params['*'], pathname)
  // Router state has to survive the hop: callers pass intent through it
  // (openAdd opens a create drawer, the subscription flow passes its report
  // type), and dropping it silently landed users on a page that ignored them.
  return <Navigate to={`/b/${bId}${section}${splat}${search}${hash}`} state={state} replace />
}
